import Chart from "chart.js/auto";
import atlas from "us-atlas/states-10m.json";
import { topojson } from "chartjs-chart-geo";
import {
  topojsonStateName,
  stateAbbreviation,
} from "../../utilities/charts/state_abbreviation_map";
import ChartControllerBase from "./chart_controller_base";
import {
  FILTERED_OUT_COLOR,
  getFocusedStates,
  onFocusedStatesChange,
} from "../../utilities/charts/payer_profile_state_filter";

// Lean US choropleth base for charts driven entirely by a chart-data JSON
// attribute, with no payer-type/drug toggles, click-through modals, or live
// recalculation (unlike us_state_chart_controller_base).
export default class extends ChartControllerBase {
  connect() {
    this.params = JSON.parse(this.data.get("chartData"));
    this.stateLinkBase = this.data.get("stateLinkBase");
    this.stateLinkAnchor = this.data.get("stateLinkAnchor");
    this.chart = this.renderChart();

    this.resizeObserver = new ResizeObserver(() => this.chart?.resize());
    this.resizeObserver.observe(this.element);

    // Redraw when the account manager filter changes which states are in focus
    this.stopFollowingFilter = onFocusedStatesChange(() => this.chart?.update());
  }

  disconnect() {
    this.stopFollowingFilter?.();
    this.resizeObserver?.disconnect();
    this.chart?.destroy();
  }

  valueForState(/* abbreviation */) {
    throw new Error("Implement in child!");
  }

  colorForState(/* abbreviation */) {
    throw new Error("Implement in child!");
  }

  legendLabels(/* chart */) {
    throw new Error("Implement in child!");
  }

  tooltipLabel(/* item */) {
    throw new Error("Implement in child!");
  }

  // The state's own color, or gray when the account manager filter leaves it out
  displayColorForState(abbreviation) {
    const focused = getFocusedStates();

    return focused && !focused.has(abbreviation)
      ? FILTERED_OUT_COLOR
      : this.colorForState(abbreviation);
  }

  renderChart() {
    return new Chart(this.appendCanvas(), {
      type: "choropleth",
      options: {
        responsive: true,
        maintainAspectRatio: false,
        onClick: (event, elements) => {
          if (!this.stateLinkBase || !elements.length) return;
          const abbreviation = stateAbbreviation(elements[0].element.feature);

          if (abbreviation) {
            const anchor = this.stateLinkAnchor ? `#${this.stateLinkAnchor}` : "";

            window.location.href = this.stateLinkBase + abbreviation + anchor;
          }
        },
        onHover: (event, elements) => {
          event.native.target.style.cursor = this.stateLinkBase && elements.length ? "pointer" : "";
        },
        plugins: {
          legend: {
            display: true,
            position: "bottom",
            labels: {
              usePointStyle: true,
              generateLabels: chart => this.legendLabels(chart),
            },
            onClick: () => {},
          },
          tooltip: {
            callbacks: {
              title: items => topojsonStateName(items[0].raw.feature),
              label: item => this.tooltipLabel(item),
            },
          },
        },
        scales: {
          projection: {
            axis: "x",
            projection: "albersUsa",
          },
          color: {
            display: false,
            axis: "x",
          },
        },
      },
      data: {
        labels: this.states.map(topojsonStateName),
        datasets: [
          {
            label: "States",
            outline: this.nation,
            data: this.states.map(state => ({
              feature: state,
              value: this.valueForState(stateAbbreviation(state)),
            })),
            backgroundColor: datum =>
              this.displayColorForState(stateAbbreviation(datum.raw.feature)),
            // chartjs-chart-geo draws no borders by default, so neighboring
            // states in the same saturated color run together.
            borderColor: "#ffffff",
            borderWidth: 1.5,
          },
        ],
      },
    });
  }

  get nation() {
    return topojson.feature(atlas, atlas.objects.nation).features[0];
  }

  get states() {
    return topojson.feature(atlas, atlas.objects.states).features;
  }
}
